import { useState, useEffect, useRef, useCallback } from 'react';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import {
  Send, RefreshCw, Loader2, GitCommit, Sparkles, Code2,
  FileEdit, FileSearch, CheckCircle2, AlertCircle, ExternalLink,
  Eraser, ArrowDown, Undo2, ImagePlus, X, Hammer, Rocket,
  Globe, Download, FileText, FileSpreadsheet, Presentation, FileImage, FileType,
  BrainCircuit, Wand2, Settings as SettingsIcon, ClipboardList,
} from 'lucide-react';
import { VibeKonnectPhase2Panel } from '../components/devconsole/VibeKonnectPhase2Panel';
import { LocalDiffBadge } from '../components/devconsole/LocalDiffBadge';
import { PageInfoTooltip } from '../components/shared/PageInfoTooltip';
// v3.5 — Pluggable module: agent badge palette, cost formatting, slash commands.
// This is the single import surface. Any host project drops <VibeKonnect config={...}>
// around this same DevConsolePage and gets identical behavior with their own theme/auth.
import {
  VibeKonnect,
  AGENT_BADGES,
  formatCostUsd,
  computeCumulativeCost,
  DEFAULT_KONNECT_CONFIG,
  SLASH_COMMANDS,
} from '../modules/vibeconnect';
import { MODEL_PRICING } from '../modules/vibeconnect/costMeter';
import { matchSlashCommands } from '../modules/vibeconnect/slashCommands';
import { FileChangeCard } from '../modules/vibeconnect/FileChangeCard';
import { PlanChecklistCard } from '../modules/vibeconnect/PlanChecklistCard';
import VibeKonnectSettingsModal from '../modules/vibeconnect/SettingsModal';

const API = process.env.REACT_APP_BACKEND_URL;
// Always iframe the SAME ORIGIN so it works on production (konnect.kaharian.com)
// and preview alike — bypasses X-Frame-Options SAMEORIGIN restrictions.
const SAME_ORIGIN_PREVIEW = (typeof window !== 'undefined' ? window.location.origin : '') + '/dashboard';

const newSessionId = () => 'sess_' + Math.random().toString(36).slice(2, 10) + Date.now().toString(36);

// ─── Sub-components ────────────────────────────────────────────────────

const TypingDots = () => (
  <span className="inline-flex gap-1 items-center" data-testid="vibe-typing">
    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: '0ms' }} />
    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: '200ms' }} />
    <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" style={{ animationDelay: '400ms' }} />
  </span>
);

const UserBubble = ({ text }) => (
  <div className="flex justify-end" data-testid="chat-user-bubble">
    <div className="max-w-[85%] rounded-2xl rounded-tr-sm px-4 py-2.5 bg-blue-900/70 border border-blue-700/60 text-blue-50 text-sm whitespace-pre-wrap leading-relaxed">
      {text}
    </div>
  </div>
);

const ToolEventLine = ({ event }) => {
  const tool = event.tool;
  let Icon, verb, tone, label;
  if (tool === 'write_file') {
    Icon = FileEdit; verb = 'Editing'; tone = 'text-amber-300 border-amber-800/60 bg-amber-950/30'; label = event.path || '';
  } else if (tool === 'read_file') {
    Icon = FileSearch; verb = 'Reading'; tone = 'text-sky-300 border-sky-800/60 bg-sky-950/30'; label = event.path || '';
  } else if (tool === 'search_code') {
    Icon = FileSearch; verb = 'Searched'; tone = 'text-violet-300 border-violet-800/60 bg-violet-950/30'; label = `'${event.query}' → ${event.matches || 0} files`;
  } else if (tool === 'web_search') {
    Icon = Globe; verb = 'Web search'; tone = 'text-cyan-300 border-cyan-800/60 bg-cyan-950/30'; label = event.query || '';
  } else if (tool === 'code_execution' || tool === 'bash_code_execution') {
    Icon = Code2; verb = 'Ran Python'; tone = 'text-emerald-300 border-emerald-800/60 bg-emerald-950/30';
    label = (event.query || '').slice(0, 80);
  } else if (tool && tool.startsWith('generate_')) {
    Icon = Sparkles; verb = (event.ok ? 'Generated' : 'Failed') + ' ' + tool.replace('generate_', '');
    tone = event.ok ? 'text-emerald-300 border-emerald-800/60 bg-emerald-950/30' : 'text-red-300 border-red-800/60 bg-red-950/30';
    label = event.title || event.filename || event.error || '';
  } else {
    Icon = FileSearch; verb = tool || 'tool'; tone = 'text-zinc-300 border-zinc-700/60 bg-zinc-900/40'; label = '';
  }
  return (
    <div className={'flex items-center gap-2 text-[11px] font-mono px-2.5 py-1 rounded border ' + tone}>
      <Icon className="h-3 w-3 shrink-0" />
      <span className="opacity-70">{verb}</span>
      <span className="truncate">{label}</span>
      {event.bytes ? <span className="opacity-50">· {event.bytes}b</span> : null}
    </div>
  );
};

// Live activity ticker — elapsed timer + blinking cursor + dynamic phrase
const LiveTicker = ({ action, elapsed }) => {
  const mins = Math.floor((elapsed || 0) / 60);
  const secs = Math.floor((elapsed || 0) % 60);
  const elapsedStr = `${mins}:${String(secs).padStart(2, '0')}`;
  return (
    <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-400" data-testid="vibe-live-ticker">
      <TypingDots />
      <span className="italic flex-1 truncate">{action || 'Working…'}</span>
      <span className="font-mono tabular-nums text-emerald-400/80 shrink-0">{elapsedStr}</span>
    </div>
  );
};

// File icon picker for artifact cards
const ArtifactIcon = ({ type }) => {
  const t = (type || '').toLowerCase();
  if (t === 'pdf') return <FileType className="h-4 w-4 text-rose-400" />;
  if (t === 'xlsx' || t === 'excel') return <FileSpreadsheet className="h-4 w-4 text-emerald-400" />;
  if (t === 'docx' || t === 'word') return <FileText className="h-4 w-4 text-sky-400" />;
  if (t === 'pptx' || t === 'powerpoint') return <Presentation className="h-4 w-4 text-orange-400" />;
  if (t === 'png' || t === 'jpg' || t === 'jpeg' || t === 'image') return <FileImage className="h-4 w-4 text-fuchsia-400" />;
  return <FileText className="h-4 w-4 text-zinc-400" />;
};

const fmtBytes = (n) => {
  if (!n) return '';
  if (n < 1024) return n + ' B';
  if (n < 1024 * 1024) return (n / 1024).toFixed(1) + ' KB';
  return (n / 1024 / 1024).toFixed(1) + ' MB';
};

const ArtifactCard = ({ artifact }) => {
  const typeLabel = (artifact.type || '').toUpperCase();
  // url is /api/uploads/... — make absolute so it works with download attribute
  const fullUrl = (artifact.url || '').startsWith('http') ? artifact.url : (API + (artifact.url || ''));
  return (
    <a
      href={fullUrl}
      target="_blank"
      rel="noreferrer"
      download={artifact.filename}
      className="group flex items-center gap-3 px-3 py-2 rounded-lg border border-zinc-700/70 bg-zinc-800/60 hover:bg-zinc-800 hover:border-emerald-700/60 transition-colors"
      data-testid={`vibe-artifact-${artifact.filename}`}
    >
      <div className="w-9 h-9 rounded-md bg-zinc-900/80 border border-zinc-700/60 flex items-center justify-center shrink-0">
        <ArtifactIcon type={artifact.type} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="text-xs font-medium text-zinc-100 truncate">{artifact.title || artifact.filename}</div>
        <div className="text-[10px] text-zinc-400 flex items-center gap-1.5">
          <span className="font-mono">{typeLabel}</span>
          {artifact.size_bytes ? <><span>·</span><span>{fmtBytes(artifact.size_bytes)}</span></> : null}
        </div>
      </div>
      <Download className="h-3.5 w-3.5 text-zinc-500 group-hover:text-emerald-400 transition-colors shrink-0" />
    </a>
  );
};

const CitationsBlock = ({ citations }) => {
  if (!citations || citations.length === 0) return null;
  return (
    <div className="mt-2" data-testid="vibe-citations">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1 flex items-center gap-1">
        <Globe className="h-3 w-3" /> Sources ({citations.length})
      </div>
      <div className="space-y-1">
        {citations.slice(0, 6).map((c, i) => (
          <a key={i} href={c.url} target="_blank" rel="noreferrer"
             className="flex items-start gap-1.5 text-[11px] text-zinc-300 hover:text-emerald-300 transition-colors group"
             data-testid={`vibe-citation-${i}`}>
            <span className="text-zinc-600 font-mono shrink-0 mt-px">[{i + 1}]</span>
            <span className="flex-1 min-w-0">
              <span className="block truncate group-hover:underline">{c.title || c.url}</span>
              <span className="block text-[9px] text-zinc-500 truncate">{c.url}</span>
            </span>
          </a>
        ))}
      </div>
    </div>
  );
};

const AssistantBubble = ({ turn, onCommit, onApplyPreview, onDeployNow, committing, applying, deploying, sessionStaged, sessionIdForCards }) => {
  const showCommit = turn.done && turn.staged && turn.staged.length > 0 && !turn.committed;
  const showCommitted = turn.committed;
  // v3.5 — agent badge (from Orchestrator)
  const agentBadge = turn.agent ? AGENT_BADGES[turn.agent] : null;

  return (
    <div className="flex flex-col gap-2" data-testid="chat-assistant-bubble">
      <div className="flex items-start gap-2">
        <div className="shrink-0 w-7 h-7 rounded-lg bg-emerald-600/20 border border-emerald-700/50 flex items-center justify-center">
          <Sparkles className="h-3.5 w-3.5 text-emerald-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 mb-1 flex-wrap">
            <span className="text-[10px] uppercase tracking-wider text-zinc-500">VibeKonnect</span>
            {agentBadge ? (
              <span
                data-testid={`agent-badge-${turn.agent}`}
                className={`text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border ${agentBadge.color}`}
                title={`Routed to the ${agentBadge.label} agent based on: ${turn.routeReason || 'n/a'}`}
              >
                <span className="mr-1">{agentBadge.icon}</span>{agentBadge.label}
              </span>
            ) : null}
            {turn.modelUsed ? (
              <span className="text-[9px] uppercase tracking-wider text-zinc-500 font-mono" title={turn.modelUsed}>
                {(MODEL_PRICING[turn.modelUsed]?.label) || turn.modelUsed.split('-')[1] || 'Claude'}
              </span>
            ) : null}
            {turn.usage && turn.usage.cost_usd > 0 ? (
              <span className="text-[9px] text-zinc-500 font-mono ml-auto" title={`${(turn.usage.input_tokens||0).toLocaleString()} in + ${(turn.usage.output_tokens||0).toLocaleString()} out tokens`}>
                {formatCostUsd(turn.usage.cost_usd)}
              </span>
            ) : null}
          </div>
          <div className="rounded-2xl rounded-tl-sm px-4 py-2.5 bg-zinc-800/70 border border-zinc-700/60 text-zinc-100 text-sm whitespace-pre-wrap leading-relaxed">
            {/* v3.8 — Interactive plan.md checklist card, rendered from the
                assistant's numbered task list. Client-side toggles only. */}
            {turn.text && turn.agent === 'architect' ? (
              <PlanChecklistCard markdown={turn.text} turnId={turn.id} />
            ) : null}
            {turn.text ? (
              <>
                <span>{turn.text}</span>
                {turn.streaming && (
                  <span className="inline-block w-[2px] h-[1em] bg-emerald-400 ml-[1px] align-middle animate-pulse" />
                )}
              </>
            ) : (turn.streaming ? (
              <span className="flex items-center gap-2">
                <TypingDots />
                <span className="text-xs text-zinc-400 italic">{turn.currentAction || 'Thinking…'}</span>
              </span>
            ) : <span className="opacity-50">…</span>)}
            {turn.streaming ? (
              <LiveTicker action={turn.currentAction} elapsed={turn.elapsedSeconds} />
            ) : null}
          </div>
          {turn.toolEvents && turn.toolEvents.length > 0 ? (
            <div className="mt-2 space-y-1">
              {turn.toolEvents.map((ev, i) => <ToolEventLine key={i} event={ev} />)}
            </div>
          ) : null}
          {turn.artifacts && turn.artifacts.length > 0 ? (
            <div className="mt-2 space-y-1.5" data-testid="vibe-artifacts">
              {turn.artifacts.map((a, i) => <ArtifactCard key={i} artifact={a} />)}
            </div>
          ) : null}
          <CitationsBlock citations={turn.citations} />
          {turn.error ? (
            <div className="mt-2 flex items-start gap-2 p-2 rounded bg-red-950/40 border border-red-900 text-red-200 text-xs">
              <AlertCircle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
              <span className="font-mono break-all">{turn.error}</span>
            </div>
          ) : null}
          {showCommit ? (
            <div className="mt-3 p-3 rounded-lg bg-emerald-950/40 border border-emerald-800/60">
              <div className="flex items-center gap-2 text-xs text-emerald-200 mb-2">
                <CheckCircle2 className="h-3.5 w-3.5" />
                <span className="font-medium">{sessionStaged.length} file{sessionStaged.length !== 1 ? 's' : ''} ready</span>
              </div>
              <div className="space-y-1 mb-3 max-h-64 overflow-y-auto">
                {sessionStaged.map((p) => (
                  <FileChangeCard
                    key={p}
                    filePath={p}
                    sessionId={sessionIdForCards}
                    kind="modified"
                  />
                ))}
              </div>
              <div className="flex flex-col gap-1.5">
                <Button
                  size="sm"
                  data-testid="apply-preview-btn"
                  onClick={onApplyPreview}
                  disabled={applying || committing || deploying}
                  className="bg-sky-700 hover:bg-sky-600 h-8 text-xs gap-1.5 w-full"
                  title="Write staged files to disk and rebuild — lets you preview BEFORE pushing to GitHub"
                >
                  {applying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Hammer className="h-3.5 w-3.5" />}
                  Preview Live (no commit)
                </Button>
                <div className="grid grid-cols-2 gap-1.5">
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="commit-deploy-btn"
                    onClick={onCommit}
                    disabled={committing || applying || deploying}
                    className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700 h-8 text-xs gap-1.5"
                  >
                    {committing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitCommit className="h-3.5 w-3.5" />}
                    Commit only
                  </Button>
                  <Button
                    size="sm"
                    data-testid="deploy-now-btn"
                    onClick={onDeployNow}
                    disabled={committing || applying || deploying}
                    className="bg-emerald-600 hover:bg-emerald-500 h-8 text-xs gap-1.5"
                    title="Commit to GitHub AND run the build + restart locally (no Plesk hook needed)"
                  >
                    {deploying ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Rocket className="h-3.5 w-3.5" />}
                    Deploy Now
                  </Button>
                </div>
              </div>
            </div>
          ) : null}
          {showCommitted ? (
            <div className="mt-3 p-3 rounded-lg bg-emerald-950/40 border border-emerald-800/60 text-xs text-emerald-200">
              <div className="flex items-center gap-2 mb-1">
                <CheckCircle2 className="h-3.5 w-3.5" />
                <span className="font-medium">Deployed</span>
                {turn.commitUrl ? (
                  <a href={turn.commitUrl} target="_blank" rel="noreferrer" className="ml-auto inline-flex items-center gap-1 text-emerald-300 hover:text-emerald-200">
                    <ExternalLink className="h-3 w-3" /> view commit
                  </a>
                ) : null}
              </div>
              <div className="text-[11px] text-emerald-300/70">{turn.committedFiles?.length || 0} file{(turn.committedFiles?.length || 0) !== 1 ? 's' : ''} committed to {turn.committedBranch || 'main'}.</div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

// ─── Main page ─────────────────────────────────────────────────────────

// Inner component — all the existing logic lives here unchanged.
function DevConsoleInner() {
  const { user } = useAuth();
  const [sessionId] = useState(newSessionId);
  const [turns, setTurns] = useState([]);
  const [prompt, setPrompt] = useState('');
  const [costEstimate, setCostEstimate] = useState(null);
  const [streaming, setStreaming] = useState(false);

  // Debounced pre-send cost estimate — 0 tokens burned, safe to run on every prompt change
  useEffect(() => {
    if (!prompt.trim() || streaming) { setCostEstimate(null); return; }
    const t = setTimeout(async () => {
      try {
        const r = await axios.post(API + '/api/dev-console/agent/estimate', { prompt: prompt.trim() }, { timeout: 4000 });
        setCostEstimate(r.data);
      } catch { setCostEstimate(null); }
    }, 500);
    return () => clearTimeout(t);
  }, [prompt, streaming]);
  const [committing, setCommitting] = useState(false);
  const [iframeKey, setIframeKey] = useState(0);
  const [iframeUrl, setIframeUrl] = useState(SAME_ORIGIN_PREVIEW);
  const [mobileTab, setMobileTab] = useState('chat'); // 'chat' | 'preview' — only used on mobile
  const [lastChanges, setLastChanges] = useState([]);
  const [sessionStaged, setSessionStaged] = useState([]);
  const [attachments, setAttachments] = useState([]); // [{id, dataUrl, mediaType, base64, name}]
  const [applying, setApplying] = useState(false);
  const [deploying, setDeploying] = useState(false);
  const [buildProgress, setBuildProgress] = useState(null); // {step, status, log, files}
  const chatEndRef = useRef(null);
  const chatScrollRef = useRef(null);
  const fileInputRef = useRef(null);
  const [showScrollDown, setShowScrollDown] = useState(false);
  // v3.5 — slash-command palette state (opens when input starts with '/')
  const [slashSuggestions, setSlashSuggestions] = useState([]);
  const [slashCursor, setSlashCursor] = useState(0);
  // v3.6 — track the currently-streaming job so the Stop button can cancel it
  const [currentJobId, setCurrentJobId] = useState(null);
  // v3.10 — Settings modal (AES key vault / deploy / MCP / artifacts)
  const [settingsOpen, setSettingsOpen] = useState(false);

  const isAuthorized = !user || user.role === 'super_admin';

  const scrollToBottom = useCallback(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => { scrollToBottom(); }, [turns, scrollToBottom]);

  const handleScroll = useCallback(() => {
    const el = chatScrollRef.current;
    if (!el) return;
    const nearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    setShowScrollDown(!nearBottom);
  }, []);

  const refreshIframe = useCallback(() => {
    setIframeKey((k) => k + 1);
  }, []);

  const sendPrompt = useCallback(async (planOnly = false) => {
    if ((!prompt.trim() && attachments.length === 0) || streaming) return;
    const userText = prompt.trim() || '(image attached)';
    const sentAttachments = [...attachments];
    setPrompt('');
    setAttachments([]);
    setStreaming(true);

    const userTurn = { role: 'user', text: userText, attachments: sentAttachments.map(a => a.dataUrl), planOnly };
    const assistantTurn = {
      role: 'assistant', text: '', toolEvents: [], artifacts: [], citations: [], streaming: true,
      done: false, error: null, staged: [], currentAction: planOnly ? 'Planning (Haiku, ~$0.02)…' : 'Starting…', elapsedSeconds: 0,
      planOnly,
    };
    setTurns((prev) => [...prev, userTurn, assistantTurn]);
    const assistantIdx = turns.length + 1;

    const updateAssistant = (patch) => {
      setTurns((prev) => {
        const next = [...prev];
        if (next[assistantIdx]) next[assistantIdx] = { ...next[assistantIdx], ...patch };
        return next;
      });
    };

    try {
      // Step 1: kick off the job (returns instantly with job_id; tree fetch happens in worker)
      const startResp = await axios.post(API + '/api/dev-console/agent/chat', {
        session_id: sessionId,
        prompt: userText,
        attachments: sentAttachments.map(a => ({ media_type: a.mediaType, data: a.base64 })),
        model: selectedModel || undefined,
        plan_only: planOnly,
      }, { timeout: 20000 });
      const jobId = startResp.data.job_id;
      if (!jobId) throw new Error('No job_id returned');
      setCurrentJobId(jobId);

      // Step 2: poll fast for low-latency UX — 400ms until done
      let pollAttempts = 0;
      const maxPolls = 1500; // 10 min @ 400ms
      // First poll immediately so the user sees activity right away
      let firstPoll = true;
      while (pollAttempts < maxPolls) {
        if (!firstPoll) await new Promise((r) => setTimeout(r, 400));
        firstPoll = false;
        pollAttempts += 1;
        let s;
        try {
          const statusResp = await axios.get(
            API + '/api/dev-console/agent/chat/status?job_id=' + encodeURIComponent(jobId),
            { timeout: 15000 }
          );
          s = statusResp.data;
        } catch (pollErr) {
          // Transient error — keep polling unless it's a 404 (job expired)
          if (pollErr?.response?.status === 404) throw new Error('Job expired or not found');
          continue;
        }
        // Progressive update
        updateAssistant({
          text: s.text || '',
          toolEvents: s.tool_events || [],
          artifacts: s.artifacts || [],
          citations: s.citations || [],
          staged: s.staged || [],
          currentAction: s.current_action,
          elapsedSeconds: s.elapsed_seconds || 0,
          // v3.5 — Orchestrator metadata (agent + cost)
          agent: s.agent,
          agentLabel: s.agent_label,
          agentIcon: s.agent_icon,
          modelUsed: s.model_used,
          taskType: s.task_type,
          complexity: s.complexity,
          routeReason: s.route_reason,
          usage: s.usage || {},
        });
        if (s.done) {
          if (s.error) {
            updateAssistant({ error: s.error, streaming: false, done: true });
          } else {
            updateAssistant({ done: true, streaming: false });
            setSessionStaged(s.staged || []);
          }
          break;
        }
      }
      if (pollAttempts >= maxPolls) {
        updateAssistant({ error: 'Timed out after 10 minutes. Try a narrower prompt.', streaming: false, done: true });
      }
    } catch (e) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || e.message;
      updateAssistant({ error: detail, streaming: false, done: true });
    } finally {
      setStreaming(false);
      setCurrentJobId(null);
    }
  }, [prompt, attachments, streaming, sessionId, turns.length]);

  // Image attachment helpers
  const handleFilesSelected = useCallback(async (filesList) => {
    const files = Array.from(filesList || []).filter(f => f.type.startsWith('image/'));
    if (files.length === 0) return;
    const readers = files.slice(0, 8 - attachments.length).map((file) =>
      new Promise((resolve) => {
        const r = new FileReader();
        r.onload = () => {
          const dataUrl = r.result;
          const comma = dataUrl.indexOf(',');
          resolve({
            id: 'att_' + Math.random().toString(36).slice(2, 9),
            name: file.name,
            mediaType: file.type,
            dataUrl,
            base64: dataUrl.slice(comma + 1),
          });
        };
        r.readAsDataURL(file);
      })
    );
    const atts = await Promise.all(readers);
    setAttachments((prev) => [...prev, ...atts].slice(0, 8));
  }, [attachments.length]);

  const removeAttachment = (id) => setAttachments(prev => prev.filter(a => a.id !== id));

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items || [];
    const imgs = Array.from(items).filter(it => it.type.startsWith('image/')).map(it => it.getAsFile()).filter(Boolean);
    if (imgs.length > 0) {
      e.preventDefault();
      handleFilesSelected(imgs);
    }
  }, [handleFilesSelected]);

  // Poll a build job (used by apply-local and deploy-now)
  const pollBuild = useCallback(async (jobId, onFinish) => {
    let tries = 0;
    while (tries < 900) { // ~10min @ 700ms interval
      await new Promise(r => setTimeout(r, 700));
      tries += 1;
      try {
        const { data } = await axios.get(API + '/api/dev-console/agent/build-status?job_id=' + encodeURIComponent(jobId));
        setBuildProgress(data);
        if (data.done) { onFinish && onFinish(data); return data; }
      } catch { /* transient */ }
    }
    onFinish && onFinish({ status: 'error', error: 'Build timed out after 10 minutes' });
  }, []);

  // v3.10 — Poll the *production* deploy hook so we KNOW whether Plesk actually
  // finished (not just returned 200). Recurring P1 fix.
  const pollRemoteBuild = useCallback(async (hookUrl, remoteJobId, onFinish) => {
    if (!hookUrl || !remoteJobId) return;
    let tries = 0;
    while (tries < 600) { // ~10min @ 1s interval
      await new Promise(r => setTimeout(r, 1000));
      tries += 1;
      try {
        const { data } = await axios.get(
          API + '/api/dev-console/agent/remote-build-status'
              + '?hook_url=' + encodeURIComponent(hookUrl)
              + '&job_id='   + encodeURIComponent(remoteJobId),
        );
        setBuildProgress((p) => ({ ...(p || {}), remote: data }));
        if (data && (data.status === 'done' || data.status === 'error')) {
          onFinish && onFinish(data);
          return data;
        }
      } catch { /* transient */ }
    }
    onFinish && onFinish({ status: 'error', error: 'Production build status poll timed out' });
  }, []);

  const handleApplyPreview = useCallback(async () => {
    if (applying || sessionStaged.length === 0) return;
    setApplying(true);
    setBuildProgress({ status: 'running', step: 'Writing files to disk…', log: '', files: sessionStaged });
    try {
      // Skip `build` — dev-server hot-reload picks up file changes in ~2s, ~60x faster than craco build
      const { data } = await axios.post(API + '/api/dev-console/agent/apply-local', {
        session_id: sessionId, build: false, restart: true,
      });
      toast.success('Applied ' + (data.files?.length || 0) + ' files — hot-reloading…');
      // Give webpack ~2.5s to recompile then refresh the iframe
      setTimeout(() => { refreshIframe(); setBuildProgress(null); }, 2500);
      // Still poll briefly to surface any error
      pollBuild(data.job_id, () => {}).catch(() => {});
    } catch (e) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || e.message;
      toast.error('Apply failed: ' + detail);
      setBuildProgress({ status: 'error', step: 'Apply failed', log: '', error: detail });
    } finally {
      setApplying(false);
    }
  }, [applying, sessionStaged, sessionId, pollBuild, refreshIframe]);

  const handleDeployNow = useCallback(async () => {
    if (deploying || sessionStaged.length === 0) return;
    setDeploying(true);
    setBuildProgress({ status: 'running', step: 'Committing to GitHub…', log: '', files: sessionStaged });
    try {
      const { data } = await axios.post(API + '/api/dev-console/agent/deploy-now', { session_id: sessionId });
      toast.success('Committed — building & restarting…');
      setLastChanges(data.files || []);
      setSessionStaged([]);
      // Mark assistant bubble as committed
      setTurns((prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === 'assistant' && next[i].done && !next[i].committed) {
            next[i] = { ...next[i], committed: true, commitUrl: data.commit_url, committedFiles: data.files, committedBranch: data.branch };
            break;
          }
        }
        return next;
      });
      if (data.build_job_id) {
        await pollBuild(data.build_job_id, (final) => {
          if (final.status === 'rolled_back') {
            const reason = final.auto_rollback?.reason || final.error || 'health check failed';
            toast.error('🛡️ Auto-rollback: deploy failed health check → reverted. ' + reason, { duration: 15000 });
            // Refresh preview so user sees the pre-deploy state
            setTimeout(() => refreshIframe(), 1500);
          } else if (final.status === 'done') {
            const health = final.deploy_health;
            if (health && health.checks && health.checks.every(c => c.ok)) {
              toast.success('✓ Deployed & verified live (all health checks passed)');
            } else {
              toast.success('Local build done — refreshing preview');
            }
            setTimeout(() => refreshIframe(), 500);
          } else {
            toast.error('Build failed after commit: ' + (final.error || 'unknown'));
          }
        });
      }
      // v3.10 — Actually verify the PRODUCTION Plesk build. Previously we
      // trusted the HTTP 200 from the deploy hook (a recurring P1 bug where
      // Plesk silently failed but VibeKonnect claimed success).
      if (data.production && data.production.hook_url && data.production.remote_job_id) {
        toast.info('Production build queued on Plesk — verifying…');
        pollRemoteBuild(data.production.hook_url, data.production.remote_job_id, (prodFinal) => {
          if (prodFinal.status === 'done') {
            toast.success('Production build verified live on Plesk.');
          } else {
            toast.error('PRODUCTION build failed on Plesk: ' + (prodFinal.error || 'see build-status log'));
          }
        }).catch(() => {});
      } else if (data.production_via_restart_txt) {
        // Running on the production host itself — restart.txt already bumped Passenger.
        toast.success('Committed to Plesk — Passenger reloading via restart.txt bump.');
      } else if (data.production_error) {
        toast.warning('Cross-server auto-deploy skipped: ' + data.production_error);
      }
    } catch (e) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || e.message;
      toast.error('Deploy failed: ' + detail);
    } finally {
      setDeploying(false);
    }
  }, [deploying, sessionStaged, sessionId, pollBuild, pollRemoteBuild, refreshIframe]);

  const handleCommit = useCallback(async () => {
    if (committing || sessionStaged.length === 0) return;
    setCommitting(true);
    try {
      const { data } = await axios.post(API + '/api/dev-console/agent/commit', {
        session_id: sessionId,
      });
      toast.success('Deployed to GitHub — Plesk auto-deploy triggered. Reloading in 90s…');
      setLastChanges(data.files || []);
      setSessionStaged([]);

      // Mark the latest assistant turn as committed
      setTurns((prev) => {
        const next = [...prev];
        for (let i = next.length - 1; i >= 0; i--) {
          if (next[i].role === 'assistant' && next[i].done && !next[i].committed) {
            next[i] = {
              ...next[i],
              committed: true,
              commitUrl: data.commit_url,
              committedFiles: data.files,
              committedBranch: data.branch,
            };
            break;
          }
        }
        return next;
      });

      // Auto-refresh iframe after a delay (give Plesk a moment)
      setTimeout(() => refreshIframe(), 4000);
      // Auto-reload the OUTER page so the user sees their deployed change live.
      // 90s gives Plesk's npm install + craco build + backend restart time to finish.
      setTimeout(() => {
        toast.info('Reloading to load deployed version…');
        setTimeout(() => window.location.reload(), 1500);
      }, 90000);
    } catch (e) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || e.message;
      toast.error('Commit failed: ' + detail);
    } finally {
      setCommitting(false);
    }
  }, [committing, sessionStaged.length, sessionId, refreshIframe]);

  const handleClearChat = useCallback(async () => {
    try {
      await axios.post(API + '/api/dev-console/agent/reset', { session_id: sessionId });
    } catch (_e) { /* best-effort */ }
    setTurns([]);
    setSessionStaged([]);
    setLastChanges([]);
    toast.info('Chat cleared');
  }, [sessionId]);

  const handleClearMemory = useCallback(async () => {
    const ok = window.confirm(
      "Wipe ALL of VibeKonnect's persistent memory of past conversations with you?\n\n" +
      "This deletes the entire stored chat history from the database. " +
      "VibeKonnect will start fresh on the next message.\n\n" +
      "This cannot be undone."
    );
    if (!ok) return;
    try {
      await axios.post(API + '/api/dev-console/agent/memory/clear', {});
      setTurns([]);
      setSessionStaged([]);
      setLastChanges([]);
      toast.success('Persistent memory wiped — VibeKonnect has forgotten everything.');
    } catch (e) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || e.message;
      toast.error('Could not clear memory: ' + detail);
    }
  }, []);

  // Rollback last deploy
  const [rollingBack, setRollingBack] = useState(false);
  const [phase2Open, setPhase2Open] = useState(false);
  // v3.4: user-selectable Claude model. Loaded from /models/available on mount.
  // Null = server default. Choice is persisted per browser via localStorage.
  const [availableModels, setAvailableModels] = useState([]);
  const [selectedModel, setSelectedModel] = useState(() => {
    try { return localStorage.getItem('vibekonnect_model') || ''; } catch { return ''; }
  });
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(API + '/api/dev-console/agent/models/available');
        setAvailableModels(r.data?.models || []);
      } catch { /* ignore — dropdown just stays empty */ }
    })();
  }, []);
  useEffect(() => {
    try { if (selectedModel) localStorage.setItem('vibekonnect_model', selectedModel); else localStorage.removeItem('vibekonnect_model'); } catch { /* ignore */ }
  }, [selectedModel]);

  // v3.3 (2026-07-01): allow the Phase-2 Threads panel to hydrate the main chat
  // pane with a saved thread's history without disrupting the active session id.
  const loadThreadIntoChat = useCallback((thread) => {
    const hist = thread?.history || [];
    // Convert the raw Claude history into the UI's `turns` structure
    // (each turn = { role, text, tool_events, image_attachments, ... }).
    const uiTurns = hist.map((msg, idx) => {
      const isText = typeof msg.content === 'string';
      const text = isText
        ? msg.content
        : (msg.content || []).filter((b) => b?.type === 'text').map((b) => b.text).join('\n');
      const imgs = isText ? [] : (msg.content || []).filter((b) => b?.type === 'image').map((b) => ({ base64: b?.source?.data, mediaType: b?.source?.media_type }));
      return { id: 'hist_' + idx, role: msg.role, text, tool_events: [], image_attachments: imgs, done: true };
    });
    setTurns(uiTurns);
    setPhase2Open(false);
    toast.success(`Loaded thread "${thread.name}" · ${uiTurns.length} messages`);
  }, []);
  const handleRollback = useCallback(async () => {
    if (rollingBack) return;
    let preview = null;
    try {
      const { data } = await axios.get(API + '/api/dev-console/agent/rollback/preview');
      preview = data;
    } catch (e) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || e.message;
      toast.error('Could not preview rollback: ' + detail);
      return;
    }
    if (!preview || !preview.can_rollback) {
      toast.error('Nothing to roll back.');
      return;
    }
    const confirmMsg =
      'Roll back the latest deploy?\n\n' +
      'Commit: ' + (preview.head_message || preview.head_sha.slice(0, 7)) + '\n' +
      'Author: ' + preview.head_author + '\n' +
      'Files affected: ' + (preview.files_changed?.length || 0) + '\n\n' +
      'A new revert commit will be created on top of HEAD — nothing is force-pushed.';
    if (!window.confirm(confirmMsg)) return;
    setRollingBack(true);
    try {
      const { data } = await axios.post(API + '/api/dev-console/agent/rollback');
      toast.success('Rolled back. New commit: ' + (data.new_commit_sha || '').slice(0, 7));
      setTurns((prev) => [...prev, {
        role: 'assistant',
        text: 'Rolled back the previous deploy. A revert commit was added on top of HEAD; Plesk will redeploy in a moment.',
        toolEvents: [],
        done: true,
        streaming: false,
        committed: true,
        commitUrl: data.new_commit_url,
        committedFiles: [],
        committedBranch: data.branch,
      }]);
      setLastChanges([]);
      setTimeout(() => refreshIframe(), 4000);
    } catch (e) {
      const detail = (e && e.response && e.response.data && e.response.data.detail) || e.message;
      toast.error('Rollback failed: ' + detail);
    } finally {
      setRollingBack(false);
    }
  }, [rollingBack, refreshIframe]);

  // v3.6 — Stop-Generation button
  const handleStopGeneration = useCallback(async () => {
    if (!currentJobId) return;
    try {
      await axios.post(API + '/api/dev-console/agent/chat/cancel', { job_id: currentJobId });
      toast.info('Stopping VibeKonnect…');
    } catch (e) {
      toast.error('Cancel failed: ' + (e.response?.data?.detail || e.message));
    }
  }, [currentJobId]);

  // v3.5 — Slash-command runner. Delegates each command to the correct handler.
  const runSlashCommand = useCallback((cmd) => {
    setSlashSuggestions([]);
    setPrompt('');
    const ctx = {
      setPrompt,
      sendPrompt: (text) => { setPrompt(text); setTimeout(() => { sendPrompt(text); }, 0); },
      navigate: (p) => { window.location.href = p; },
      openPanel: (id) => setMobileTab(id),
      handleReset: () => { setTurns([]); toast.success('Session cleared'); },
      showToast: (m) => toast.success(m),
      deploy: () => handleDeployNow?.(),
      preview: () => handleApplyPreview?.(),
      fork: () => toast.info('Use the Threads tab on the right panel to fork.'),
      clearMemory: () => handleClearMemory?.(),
      openSettings: () => setSettingsOpen(true),
      showCost: () => {
        const total = computeCumulativeCost(turns.map((t) => ({ usage: t.usage })));
        toast.info(
          `Session cost: ${formatCostUsd(total.cost_usd)} — ` +
          `${total.input_tokens.toLocaleString()} in / ${total.output_tokens.toLocaleString()} out tokens, ` +
          `${total.cache_read_input_tokens.toLocaleString()} cached.`,
          { duration: 8000 },
        );
      },
    };
    try { cmd.action(ctx); } catch (e) { toast.error(`Command failed: ${e.message || e}`); }
  }, [turns]);

  const onKeyDown = (e) => {
    // Slash-command keyboard handling — arrows navigate, Enter runs.
    if (slashSuggestions.length > 0) {
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSlashCursor((c) => Math.min(c + 1, slashSuggestions.length - 1));
        return;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSlashCursor((c) => Math.max(c - 1, 0));
        return;
      }
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        runSlashCommand(slashSuggestions[slashCursor]);
        return;
      }
      if (e.key === 'Escape') {
        setSlashSuggestions([]);
        return;
      }
    }
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault();
      sendPrompt();
    }
  };

  if (user && user.role !== 'super_admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)] bg-zinc-950 text-zinc-100" data-testid="dev-console-page">
      {/* Header */}
      <div className="flex items-center justify-between px-3 sm:px-5 py-2.5 sm:py-3 border-b border-zinc-800 bg-zinc-900/80 backdrop-blur gap-2">
        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-emerald-500/30 to-teal-600/20 border border-emerald-700/50 flex items-center justify-center shrink-0">
            <Code2 className="h-4 w-4 text-emerald-400" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-semibold tracking-tight truncate flex items-center">VibeKonnect<PageInfoTooltip pageKey="vibekonnect" /></div>
            <div className="text-[10px] text-zinc-500 font-mono truncate hidden sm:block">AI builder · powered by Claude</div>
          </div>
          <Badge variant="outline" className="ml-2 sm:ml-3 font-mono text-[10px] border-zinc-700 text-zinc-400 hidden md:inline-flex">
            kaharian614/Kaharian-Konnect @ main
          </Badge>
        </div>
        <div className="flex items-center gap-1.5">
          {/* v3.6 — Context-usage meter. Anthropic Sonnet/Opus have 200k window;
              Haiku 200k too. Shows the largest single-turn input_tokens so far
              relative to that budget, with soft (75%) / hard (90%) / critical (98%)
              thresholds prompting the user to /fork. */}
          {(() => {
            const perTurnMax = Math.max(0, ...turns.map((t) => Number(t?.usage?.input_tokens || 0)));
            if (perTurnMax === 0) return null;
            const budget = 200_000; // Sonnet 4.5 & Opus 4.1 both — Haiku is same.
            const pct = Math.min(100, Math.round((perTurnMax / budget) * 100));
            const color =
              pct >= 90 ? 'text-red-400   border-red-800   bg-red-950/40'
              : pct >= 75 ? 'text-amber-300 border-amber-800 bg-amber-950/40'
              : 'text-zinc-400  border-zinc-800  bg-zinc-950';
            return (
              <div
                data-testid="vibe-context-meter"
                className={`hidden md:flex items-center gap-1 h-8 px-2 rounded-md border text-[10px] font-mono ${color}`}
                title={`Peak ${perTurnMax.toLocaleString()} / ${budget.toLocaleString()} tokens (${pct}% of context window). ${pct >= 75 ? 'Consider /fork to start a fresh context.' : ''}`}
              >
                <span>ctx {pct}%</span>
              </div>
            );
          })()}
          {/* v3.5 — Session cost meter (real-time, from Orchestrator usage tracking).
              Hidden until first turn has produced a cost. */}
          {(() => {
            const total = computeCumulativeCost(turns.map((t) => ({ usage: t.usage })));
            if (total.cost_usd <= 0) return null;
            const cacheHit = (() => {
              const t = total.cache_read_input_tokens + total.input_tokens;
              return t === 0 ? 0 : Math.round((total.cache_read_input_tokens / t) * 100);
            })();
            return (
              <div
                data-testid="vibe-cost-meter"
                className="hidden md:flex items-center gap-1 h-8 px-2 rounded-md border border-zinc-800 bg-zinc-950 text-[10px] font-mono text-zinc-400"
                title={`${total.input_tokens.toLocaleString()} in / ${total.output_tokens.toLocaleString()} out tokens · ${cacheHit}% cache hit`}
              >
                <span className="text-emerald-400">{formatCostUsd(total.cost_usd)}</span>
                <span className="text-zinc-600">·</span>
                <span>{cacheHit}%↺</span>
              </div>
            );
          })()}
          {/* v3.4 — Model selector (Claude Fable 5 / Anthropic direct / Kaharian Gateway).
              NEVER Emergent LLM per product spec. */}
          {availableModels.length > 0 && (
            <select
              data-testid="vibekonnect-model-select"
              value={selectedModel}
              onChange={(e) => setSelectedModel(e.target.value)}
              disabled={streaming}
              title="Choose which model VibeKonnect will use for this and future turns. Persisted per browser."
              className="hidden md:inline-flex h-8 rounded-md border border-zinc-700 bg-zinc-900 text-zinc-200 text-[11px] px-2 focus:outline-none focus:border-emerald-600 max-w-[220px] truncate"
            >
              <option value="">Default (server-picked)</option>
              <optgroup label="Anthropic API (direct)">
                {availableModels.filter(m => m.provider === 'anthropic').map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}{m.recommended ? '  ★' : ''}
                  </option>
                ))}
              </optgroup>
              <optgroup label="Kaharian Gateway (fallback ladder)">
                {availableModels.filter(m => m.provider === 'kaharian').map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.label}
                  </option>
                ))}
              </optgroup>
            </select>
          )}
          {/* Mobile tab switcher */}
          <div className="lg:hidden flex rounded-md border border-zinc-700 bg-zinc-800/50 p-0.5">
            <button
              type="button"
              data-testid="mobile-tab-chat"
              onClick={() => setMobileTab('chat')}
              className={'px-2.5 py-1 text-[11px] rounded transition-colors ' + (mobileTab === 'chat' ? 'bg-emerald-600/30 text-emerald-200' : 'text-zinc-400')}
            >Chat</button>
            <button
              type="button"
              data-testid="mobile-tab-preview"
              onClick={() => setMobileTab('preview')}
              className={'px-2.5 py-1 text-[11px] rounded transition-colors ' + (mobileTab === 'preview' ? 'bg-emerald-600/30 text-emerald-200' : 'text-zinc-400')}
            >Preview</button>
          </div>
          <LocalDiffBadge />
          <Button
            size="sm" variant="ghost"
            data-testid="vk-settings-btn"
            onClick={() => setSettingsOpen(true)}
            className="text-zinc-400 hover:text-emerald-200 hover:bg-emerald-950/40 h-8 text-xs gap-1 px-2"
            title="API keys · Deploy targets · MCP servers · Artifacts"
          >
            <SettingsIcon className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Settings</span>
          </Button>
          <Button
            size="sm" variant="ghost"
            data-testid="phase2-open-btn"
            onClick={() => setPhase2Open(true)}
            className="text-emerald-300 hover:text-emerald-200 hover:bg-emerald-950/40 h-8 text-xs gap-1 px-2"
            title="Diff · Sandbox · Chat branching · Screenshot analysis · Health digest"
          >
            <Wand2 className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Phase-2</span>
          </Button>
          <Button
            size="sm" variant="ghost"
            data-testid="rollback-btn"
            onClick={handleRollback}
            disabled={streaming || committing || rollingBack}
            title="Roll back the latest deploy on main (creates a forward-revert commit, never force-pushes)"
            className="text-amber-300 hover:text-amber-200 hover:bg-amber-950/40 h-8 text-xs gap-1 px-2"
          >
            {rollingBack ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Undo2 className="h-3.5 w-3.5" />}
            <span className="hidden sm:inline">Rollback</span>
          </Button>
          <Button
            size="sm" variant="ghost"
            data-testid="restore-demo-data-btn"
            onClick={async () => {
              if (!window.confirm('Restore default demo accounts, products and loyalty data? Existing records are kept; only missing rows are recreated.')) return;
              try {
                const r = await axios.post(API + '/api/admin/demo-data/restore');
                const s = r.data?.summary || {};
                toast.success(`Demo data restored — users:${s.users || 0} · products:${s.products || 0} · loyalty:${s.loyalty || 0}`);
              } catch (e) {
                toast.error(e.response?.data?.detail || 'Failed to restore demo data');
              }
            }}
            disabled={streaming || committing}
            className="text-emerald-400 hover:text-emerald-200 hover:bg-emerald-950/40 h-8 text-xs gap-1 px-2"
            title="Re-seed demo accounts (mina@mina.com, junnel@ilmercato.com, etc.), sample products and loyalty tiers if they were deleted."
          >
            <RefreshCw className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Restore demo</span>
          </Button>
          <Button
            size="sm" variant="ghost"
            data-testid="clear-chat-btn"
            onClick={handleClearChat}
            disabled={streaming || committing}
            className="text-zinc-400 hover:text-zinc-100 hover:bg-zinc-800 h-8 text-xs gap-1 px-2"
          >
            <Eraser className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">New chat</span>
          </Button>
          <Button
            size="sm" variant="ghost"
            data-testid="clear-memory-btn"
            onClick={handleClearMemory}
            disabled={streaming || committing}
            className="text-rose-400 hover:text-rose-200 hover:bg-rose-950/40 h-8 text-xs gap-1 px-2"
            title="Permanently wipe VibeKonnect's persistent memory of all your past conversations"
          >
            <BrainCircuit className="h-3.5 w-3.5" />
            <span className="hidden sm:inline">Forget memory</span>
          </Button>
        </div>
      </div>

      <div className="flex flex-1 min-h-0">
        {/* LEFT: Chat — full width on mobile (when active tab), 35% on desktop */}
        <section
          className={
            'lg:w-[35%] lg:min-w-[380px] lg:flex flex-col bg-zinc-950 border-r border-zinc-800 ' +
            (mobileTab === 'chat' ? 'flex w-full' : 'hidden')
          }
          data-testid="chat-panel"
        >
          <div className="flex-1 min-h-0 relative">
            <div
              ref={chatScrollRef}
              onScroll={handleScroll}
              className="absolute inset-0 overflow-y-auto px-4 py-5 space-y-4"
              data-testid="chat-scroll"
            >
              {turns.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center px-6">
                  <div className="w-14 h-14 rounded-2xl bg-emerald-600/15 border border-emerald-700/40 flex items-center justify-center mb-4">
                    <Sparkles className="h-6 w-6 text-emerald-400" />
                  </div>
                  <h3 className="text-base font-semibold text-zinc-200 mb-1">What do you want to build?</h3>
                  <p className="text-xs text-zinc-500 max-w-sm leading-relaxed">
                    Describe a feature, fix, or improvement in plain English. VibeKonnect will read the relevant files, plan the change, and stage it for one-click deploy.
                  </p>
                  <div className="mt-5 grid gap-2 w-full max-w-md">
                    {[
                      'Add a blue welcome banner on the dashboard with the user\'s name',
                      'Fix the typo on the login page',
                      'Make the sidebar collapse button more visible',
                    ].map((s) => (
                      <button
                        key={s}
                        type="button"
                        data-testid={'suggestion-' + s.slice(0, 12)}
                        onClick={() => setPrompt(s)}
                        className="text-left text-xs px-3 py-2 rounded-lg border border-zinc-800 bg-zinc-900/50 hover:bg-zinc-800/70 hover:border-zinc-700 text-zinc-400 hover:text-zinc-200 transition-colors"
                      >
                        {s}
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                turns.map((t, i) => (
                  t.role === 'user'
                    ? <UserBubble key={i} text={t.text} />
                    : <AssistantBubble
                        key={i}
                        turn={t}
                        onCommit={handleCommit}
                        onApplyPreview={handleApplyPreview}
                        onDeployNow={handleDeployNow}
                        committing={committing}
                        applying={applying}
                        deploying={deploying}
                        sessionStaged={sessionStaged}
                        sessionIdForCards={sessionId}
                      />
                ))
              )}
              <div ref={chatEndRef} />
            </div>
            {showScrollDown ? (
              <button
                type="button"
                onClick={scrollToBottom}
                className="absolute bottom-4 right-4 w-8 h-8 rounded-full bg-zinc-800 border border-zinc-700 hover:bg-zinc-700 flex items-center justify-center shadow-lg"
                data-testid="scroll-down-btn"
              >
                <ArrowDown className="h-4 w-4 text-zinc-300" />
              </button>
            ) : null}
          </div>

          {/* Prompt input */}
          <div className="border-t border-zinc-800 bg-zinc-900/40 p-3 sm:p-3 pb-[env(safe-area-inset-bottom,12px)]">
            {/* v3.5 — Persistent commit bar (shows any time the session has staged files
                that haven't been committed yet — regardless of which turn the user is viewing). */}
            {sessionStaged.length > 0 && !committing && !applying && !deploying ? (
              <div
                className="mb-2 p-2.5 rounded-lg bg-emerald-950/50 border border-emerald-800/70"
                data-testid="persistent-commit-bar"
              >
                <div className="flex items-center gap-2 text-[11px] text-emerald-200 mb-2">
                  <CheckCircle2 className="h-3.5 w-3.5 shrink-0" />
                  <span className="font-medium">{sessionStaged.length} file{sessionStaged.length !== 1 ? 's' : ''} staged &amp; ready</span>
                  <span className="text-emerald-400/50 font-mono ml-auto truncate hidden sm:inline">
                    {sessionStaged.slice(0, 2).join(', ')}{sessionStaged.length > 2 ? ` +${sessionStaged.length - 2}` : ''}
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5">
                  <Button
                    size="sm"
                    data-testid="persistent-preview-btn"
                    onClick={handleApplyPreview}
                    disabled={applying || committing || deploying}
                    className="bg-sky-700 hover:bg-sky-600 h-8 text-[11px] gap-1.5"
                    title="Write staged files to disk and hot-reload the preview iframe (no GitHub push)"
                  >
                    <Hammer className="h-3.5 w-3.5" />
                    Preview
                  </Button>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="persistent-commit-btn"
                    onClick={handleCommit}
                    disabled={committing || applying || deploying}
                    className="bg-zinc-800 border-zinc-700 hover:bg-zinc-700 h-8 text-[11px] gap-1.5"
                    title="Push staged files to GitHub main via the Contents API"
                  >
                    <GitCommit className="h-3.5 w-3.5" />
                    Commit
                  </Button>
                  <Button
                    size="sm"
                    data-testid="persistent-deploy-btn"
                    onClick={handleDeployNow}
                    disabled={committing || applying || deploying}
                    className="bg-emerald-600 hover:bg-emerald-500 h-8 text-[11px] gap-1.5"
                    title="Commit to GitHub AND run the build + restart in one shot"
                  >
                    <Rocket className="h-3.5 w-3.5" />
                    Deploy
                  </Button>
                </div>
              </div>
            ) : null}
            {attachments.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2" data-testid="attachment-thumbs">
                {attachments.map(a => (
                  <div key={a.id} className="relative w-16 h-16 rounded-md overflow-hidden border border-zinc-700 group">
                    <img src={a.dataUrl} alt={a.name} className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removeAttachment(a.id)}
                      className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-zinc-900/80 border border-zinc-600 opacity-0 group-hover:opacity-100 flex items-center justify-center"
                      data-testid={'remove-att-' + a.id}
                    >
                      <X className="h-2.5 w-2.5 text-zinc-300" />
                    </button>
                  </div>
                ))}
              </div>
            )}
            {/* v3.5 — slash-command palette */}
            {slashSuggestions.length > 0 ? (
              <div
                data-testid="slash-command-palette"
                className="mb-1.5 rounded-lg border border-zinc-700 bg-zinc-900/95 backdrop-blur shadow-xl max-h-[220px] overflow-y-auto"
              >
                {slashSuggestions.map((cmd, i) => (
                  <button
                    key={cmd.key}
                    type="button"
                    data-testid={`slash-cmd-${cmd.key.slice(1)}`}
                    onMouseDown={(e) => { e.preventDefault(); runSlashCommand(cmd); }}
                    className={`w-full text-left px-2.5 py-1.5 text-[11px] flex items-center gap-2 border-b border-zinc-800 last:border-0 ${
                      i === slashCursor ? 'bg-zinc-800' : 'hover:bg-zinc-800/60'
                    }`}
                  >
                    <span className="font-mono text-emerald-400 shrink-0 w-16">{cmd.key}</span>
                    <span className="text-zinc-400 truncate">{cmd.description}</span>
                  </button>
                ))}
              </div>
            ) : null}
            <Textarea
              data-testid="vibe-prompt"
              value={prompt}
              onChange={(e) => {
                const v = e.target.value;
                setPrompt(v);
                // Show slash palette when the WHOLE input begins with '/'
                if (v.startsWith('/') && !v.includes('\n')) {
                  const matches = matchSlashCommands(v.split(/\s/)[0]);
                  setSlashSuggestions(matches);
                  setSlashCursor(0);
                } else if (slashSuggestions.length > 0) {
                  setSlashSuggestions([]);
                }
              }}
              onKeyDown={onKeyDown}
              onPaste={handlePaste}
              disabled={streaming}
              placeholder="Describe what you want — or paste/drop an image. Type /  for commands. ⌘/Ctrl + Enter to send."
              rows={6}
              className="bg-zinc-950 border-zinc-800 focus:border-zinc-700 text-sm resize-y placeholder:text-zinc-600 min-h-[140px] max-h-[400px]"
            />
            {/* Live cost estimate — 0 tokens burned, updates on prompt change */}
            {costEstimate && !streaming && (
              <div
                className={
                  'mt-2 flex items-center gap-2 text-[11px] rounded-md px-2.5 py-1.5 border ' +
                  (costEstimate.tier === 'opus'
                    ? 'border-red-500/40 bg-red-500/10 text-red-300'
                    : costEstimate.tier === 'sonnet'
                    ? 'border-blue-500/30 bg-blue-500/5 text-blue-300'
                    : 'border-emerald-500/30 bg-emerald-500/5 text-emerald-300')
                }
                data-testid="vibe-cost-estimate"
              >
                <span className="font-mono text-[10px] px-1.5 py-0.5 rounded bg-zinc-800/60">
                  {costEstimate.model_label}
                </span>
                <span>
                  Est. Build: <b className="font-mono">${costEstimate.estimated_cost_usd_low.toFixed(3)}</b>
                  {' – '}
                  <b className="font-mono">${costEstimate.estimated_cost_usd_high.toFixed(3)}</b>
                </span>
                <span className="text-zinc-500">·</span>
                <span>
                  Plan-only: <b className="font-mono">${costEstimate.plan_only_cost_usd.toFixed(3)}</b>
                </span>
                {costEstimate.tier === 'opus' && (
                  <span className="ml-auto text-red-300 font-medium">⚠ Consider Plan first</span>
                )}
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => { handleFilesSelected(e.target.files); e.target.value = ''; }}
              data-testid="vibe-file-input"
            />
            <div className="flex items-center justify-between mt-2 gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() => fileInputRef.current?.click()}
                disabled={streaming || attachments.length >= 8}
                className="border-zinc-700 hover:bg-zinc-800 h-9 sm:h-8 text-xs gap-1.5 px-3"
                data-testid="vibe-attach-btn"
                title="Attach screenshots or reference images (max 8)"
              >
                <ImagePlus className="h-3.5 w-3.5" />
                <span className="hidden sm:inline">Attach</span>
              </Button>
              {streaming && currentJobId ? (
                <Button
                  size="sm"
                  type="button"
                  data-testid="vibe-stop-btn"
                  onClick={handleStopGeneration}
                  className="bg-red-600 hover:bg-red-500 h-9 sm:h-8 text-xs gap-1.5 px-4 sm:ml-auto flex-1 sm:flex-none"
                  title="Stop VibeKonnect's current turn immediately"
                >
                  <X className="h-3.5 w-3.5" />
                  Stop
                </Button>
              ) : (
                <>
                  <Button
                    size="sm"
                    variant="outline"
                    data-testid="vibe-plan-btn"
                    onClick={() => sendPrompt(true)}
                    disabled={(!prompt.trim() && attachments.length === 0) || streaming}
                    className="border-amber-500/50 text-amber-300 hover:bg-amber-500/10 h-9 sm:h-8 text-xs gap-1.5 px-3 sm:ml-auto flex-1 sm:flex-none"
                    title="Generate a plan only — Haiku (~$0.02). No files staged. Convert to Build after review."
                  >
                    <ClipboardList className="h-3.5 w-3.5" />
                    Plan (~$0.02)
                  </Button>
                  <Button
                    size="sm"
                    data-testid="vibe-send-btn"
                    onClick={() => sendPrompt(false)}
                    disabled={(!prompt.trim() && attachments.length === 0) || streaming}
                    className="bg-emerald-600 hover:bg-emerald-500 h-9 sm:h-8 text-xs gap-1.5 px-4 flex-1 sm:flex-none"
                  >
                    {streaming ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />}
                    Build
                  </Button>
                </>
              )}
            </div>
          </div>
        </section>

        {/* RIGHT: Live Preview — full width on mobile (when active tab), flex-1 on desktop */}
        <section
          className={
            'lg:flex flex-col bg-zinc-900 lg:flex-1 ' +
            (mobileTab === 'preview' ? 'flex flex-1 w-full' : 'hidden')
          }
          data-testid="preview-panel"
        >
          <div className="flex items-center justify-between px-4 py-2 border-b border-zinc-800 bg-zinc-900/80">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
              <span className="text-xs font-mono text-zinc-400 truncate">{iframeUrl}</span>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm" variant="outline"
                data-testid="iframe-refresh-btn"
                onClick={refreshIframe}
                className="border-zinc-700 hover:bg-zinc-800 h-7 text-xs gap-1.5"
                title="Refresh preview iframe"
              >
                <RefreshCw className="h-3.5 w-3.5" /> Refresh Preview
              </Button>
              <a
                href={iframeUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1 text-[11px] text-zinc-400 hover:text-zinc-200 px-2 py-1 rounded hover:bg-zinc-800"
                data-testid="iframe-open-tab"
              >
                <ExternalLink className="h-3 w-3" /> open
              </a>
            </div>
          </div>
          <div className="flex-1 min-h-0 bg-white relative">
            <iframe
              key={iframeKey}
              src={iframeUrl}
              title="Live preview"
              data-testid="preview-iframe"
              className="w-full h-full border-0"
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals allow-downloads"
            />
            {buildProgress && (buildProgress.status === 'running' || buildProgress.status === 'error') && (
              <div className="absolute left-3 right-3 bottom-3 rounded-lg border shadow-xl p-3 text-xs font-mono max-h-56 overflow-y-auto"
                   style={{ background: buildProgress.status === 'error' ? 'rgba(127,29,29,0.95)' : 'rgba(12,74,110,0.95)', color: buildProgress.status === 'error' ? '#fecaca' : '#bae6fd', borderColor: buildProgress.status === 'error' ? '#7f1d1d' : '#0369a1' }}
                   data-testid="build-progress">
                <div className="flex items-center gap-2 mb-1.5">
                  {buildProgress.status === 'error' ? <AlertCircle className="h-3.5 w-3.5" /> : <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  <span className="font-semibold">{buildProgress.step || buildProgress.status}</span>
                  <button onClick={() => setBuildProgress(null)} className="ml-auto opacity-60 hover:opacity-100"><X className="h-3 w-3" /></button>
                </div>
                {buildProgress.error && <div className="mb-1 opacity-90">{buildProgress.error}</div>}
                {buildProgress.log && <pre className="whitespace-pre-wrap opacity-80 text-[10px] leading-tight">{buildProgress.log.slice(-1500)}</pre>}
              </div>
            )}
          </div>

          {/* Changes Made */}
          <div className="border-t border-zinc-800 bg-zinc-900/60 p-3 max-h-44 overflow-y-auto" data-testid="changes-made">
            <div className="flex items-center gap-2 text-[10px] uppercase tracking-wider text-zinc-500 mb-2">
              <FileEdit className="h-3 w-3" /> Changes made (last action)
            </div>
            {lastChanges.length === 0 ? (
              <div className="text-[11px] text-zinc-600 italic">No deployed changes yet in this session.</div>
            ) : (
              <ul className="space-y-0.5">
                {lastChanges.map((p) => (
                  <li key={p} className="text-[11px] font-mono text-emerald-300/90 truncate">
                    <span className="text-zinc-600 mr-1.5">·</span>{p}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </section>
      </div>
      <VibeKonnectPhase2Panel
        open={phase2Open}
        onOpenChange={setPhase2Open}
        sessionId={sessionId}
        onLoadThread={loadThreadIntoChat}
      />
      <VibeKonnectSettingsModal open={settingsOpen} onOpenChange={setSettingsOpen} />
    </div>
  );
}

// ─── Pluggable module entry point ─────────────────────────────────────
// This is the ONLY exported symbol. Wraps the internal DevConsoleInner
// with the VibeKonnect provider so any host project can override config
// (auth adapter, theme, branding, api base, enabled agents) by rendering
// <VibeKonnect config={hostConfig}><DevConsoleInner /></VibeKonnect> instead.
//
// Konnect uses DEFAULT_KONNECT_CONFIG — external Emergent projects just
// pass their own. No other file in Konnect needs to change.
export default function DevConsolePage(props) {
  const hostConfig = props?.config || DEFAULT_KONNECT_CONFIG;
  return (
    <VibeKonnect config={hostConfig}>
      <DevConsoleInner />
    </VibeKonnect>
  );
}

